import React, { useState, useEffect } from 'react';
import { salesAPI } from '../services/api';
import { isBotica, isParking } from '../hooks/useSucursal';

// ─── Componentes por tipo de negocio ──────────────────────────────────────────
import SaleFormBotica        from '../components/SaleFormBotica';
import SaleFormTienda        from '../components/SaleFormTienda';
import SalesHistoryBotica    from '../components/SalesHistoryBotica';
import SalesHistoryTienda    from '../components/SalesHistoryTienda';

// 📱 Vista móvil optimizada (auto-detectada por ancho de pantalla ≤ 768px)
import SaleFormMobile        from '../components/SaleFormMobile';

import './SalesPage.css';

/**
 * SalesPage — Router de ventas por tipo de negocio.
 *
 * tienda_general  →  SaleFormTienda    / SalesHistoryTienda
 * botica          →  SaleFormBotica    / SalesHistoryBotica
 * parking         →  redirige a /parking (controlado por App.tsx)
 *
 * La lógica de datos (fetch, estado) se mantiene aquí y se pasa como props
 * para que los componentes hijo sean presentacionales.
 */
const SalesPage = () => {
    const [allSales, setAllSales] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [view, setView] = useState('pos');

    // 📱 Detección de pantalla móvil (≤ 768px) — reactiva al redimensionar
    const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 768px)');
        const handler = (e) => setIsMobile(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);

    // Detectar tipo de negocio una sola vez (valor estático durante la sesión)
    const esBotica = isBotica();

    const fetchAllSales = async () => {
        setIsLoading(true);
        try {
            const response = await salesAPI.getAll();
            setAllSales(response.data?.data?.sales || []);
        } catch (error) {
            console.error('Error cargando historial de ventas:', error);
            setAllSales([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (view === 'history') fetchAllSales();
    }, [view]);

    const handleSaleSuccess = (newSale) => {
        setAllSales(prevSales => [newSale, ...prevSales]);
    };

    // 📱 En móvil: siempre usar la vista móvil optimizada
    if (isMobile) {
        return <SaleFormMobile onSaleSuccess={handleSaleSuccess} />;
    }

    // ── Componentes según tipo de negocio ──────────────────────────────────
    const FormComponent    = esBotica ? SaleFormBotica    : SaleFormTienda;
    const HistoryComponent = esBotica ? SalesHistoryBotica : SalesHistoryTienda;

    // ── Vista Historial ─────────────────────────────────────────────────────
    if (view === 'history') {
        return (
            <div className="content-pane">
                <div className="sales-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <button
                            onClick={() => setView('pos')}
                            style={{
                                background: esBotica ? '#10b981' : '#6366f1',
                                color: '#fff',
                                border: 'none', borderRadius: '8px',
                                padding: '8px 16px', fontWeight: 600,
                                cursor: 'pointer', fontSize: '13px',
                                display: 'flex', alignItems: 'center', gap: '6px'
                            }}
                        >
                            ← Volver al POS
                        </button>
                        <h2 style={{
                            display: 'flex', alignItems: 'center', gap: '10px',
                            fontSize: '20px', fontWeight: 700, color: '#1e293b', margin: 0
                        }}>
                            {esBotica ? '💊' : '📋'} Historial de {esBotica ? 'Despachos' : 'Ventas'}
                        </h2>
                    </div>
                    <button
                        onClick={fetchAllSales}
                        style={{
                            background: '#f8fafc', color: '#64748b',
                            border: '1px solid #e2e8f0', borderRadius: '8px',
                            padding: '8px 14px', fontWeight: 600,
                            cursor: 'pointer', fontSize: '13px'
                        }}
                    >
                        ↺ Actualizar
                    </button>
                </div>

                <div style={{ marginTop: '20px' }}>
                    {isLoading ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            <p style={{ color: '#64748b' }}>Cargando historial...</p>
                        </div>
                    ) : (
                        <HistoryComponent sales={allSales} onRefresh={fetchAllSales} />
                    )}
                </div>
            </div>
        );
    }

    // ── Vista POS ───────────────────────────────────────────────────────────
    return (
        <FormComponent
            onSaleSuccess={handleSaleSuccess}
            onShowHistory={() => setView('history')}
        />
    );
};

export default SalesPage;